import CONFIG from 'Config/settings';
import JwtDecode from 'jwt-decode';
import { PROFILE_TYPES } from 'Redux/profile/profileConstants';
import { QUERY_PARAMS } from 'Routes/profileRoutes';
import { readCookie, writeCookie } from 'Utils/cookies/cookiesUtils';
import { isUTCDateInPast } from 'Utils/date/dateUtils';
import {
  appendQueryParamToUrl,
  getQueryStringValue,
  isTokenRequired,
  isValidIKEAUrl
} from 'Utils/url/urlUtils';

const AUTH_TOKEN_COOKIE_NAME = 'idp_reguser';
const SESSION_COOKIE_NAME = 'ikea_session';
const IKEA_FAMILY_LOYALTY_PROGRAM = 'IKEA_FAMILY';

// TODO: reading the cookies should happen through the "client-token-service". Please check: https://git.build.ingka.ikea.com/one-web-profiles/client-token-service
export const readAuthTokenCookie = () => {
  return readCookie(AUTH_TOKEN_COOKIE_NAME);
};

export const readWindowAuthTokens = () => {
  return new Promise(resolve => {
    if (window.ikea?.authentication) {
      window.ikea.authentication.getTokens((error, token) => {
        if (token && token.auth) {
          resolve(token.auth.value);
        } else {
          resolve(null);
        }
      });
    } else {
      resolve(readAuthTokenCookie());
    }
  });
};

export const writeAuthTokenCookie = accessToken => {
  writeCookie(
    AUTH_TOKEN_COOKIE_NAME,
    accessToken,
    undefined,
    `/${CONFIG.COUNTRY}`,
    true
  );
};

export const writeSessionCookie = userSession => {
  writeCookie(
    SESSION_COOKIE_NAME,
    window.btoa(unescape(encodeURIComponent(userSession))),
    undefined,
    `/${CONFIG.COUNTRY}`
  );
};

export const deleteRefreshToken = () => {
  localStorage.removeItem(`refreshToken_${CONFIG.COUNTRY.toLowerCase()}`);
};

export const validateIdToken = idToken => {
  const decodedToken = JwtDecode(idToken);
  if (
    decodedToken &&
    !isUTCDateInPast(decodedToken.exp) &&
    decodedToken.aud === CONFIG.API.AUTH0.CLIENT_ID &&
    decodedToken.iss.includes(CONFIG.API.AUTH0.DOMAIN)
  ) {
    return true;
  }
  return false;
};

export const getDecodedAuthToken = authToken => {
  const decodedToken = authToken && JwtDecode(authToken);
  if (decodedToken) {
    const customerType = decodedToken['https://accounts.ikea.com/customerType'];
    const profileType =
      customerType === 'business'
        ? PROFILE_TYPES.BUSINESS
        : decodedToken['https://accounts.ikea.com/loyaltyPrograms'].some(
            program => program === 'IKEA_FAMILY'
          )
        ? PROFILE_TYPES.FAMILY
        : PROFILE_TYPES.REGULAR;
    return {
      exp: decodedToken.exp,
      userId: decodedToken['https://accounts.ikea.com/memberId'],
      partyUid: decodedToken['https://accounts.ikea.com/partyUId'],
      profileType
    };
  }
  return {};
};

export const getRedirectUrlFromQueryStrings = queryStrings => {
  const params = decodeURIComponent(queryStrings).match(
    /state="redirectTo=([^"]*)/
  );
  return params && params.length > 1 ? params[1] : null;
};

export const getCoworkerLinkingStatusFromQueryStrings = queryStrings => {
  const params = decodeURIComponent(queryStrings).match(/icw=([^&]*)/);
  return params && params.length > 1 ? params[1] : null;
};

export const getCodeFromQueryStrings = queryStrings => {
  const params = decodeURIComponent(queryStrings).match(/code=([^&]*)/);
  return params && params.length > 1 ? params[1] : null;
};

// Will only append token query param if "url" is a valid
// IKEA URL and listed as an exact URL without wild cards.
export const appendAuthTokenToUrl = url => {
  const token = readAuthTokenCookie();
  return isValidIKEAUrl(url, true) && isTokenRequired(url) && token
    ? appendQueryParamToUrl(url, QUERY_PARAMS.TOKEN, token)
    : url;
};

export const getUtmCampaignParams = url => {
  const campaignParamsKeys = [
    QUERY_PARAMS.UTM_SOURCE,
    QUERY_PARAMS.UTM_MEDIUM,
    QUERY_PARAMS.UTM_CAMPAIGN
  ];
  const from = url.match(/from=(.*)/);
  const fromParams =
    from && from.length > 1 ? new URL(decodeURIComponent(from[1])).search : '';
  const campaignValues = campaignParamsKeys.reduce((values, key) => {
    return values.concat(getQueryStringValue(fromParams || url, key) || '');
  }, []);
  return campaignValues.join('|');
};

export const getItmCampaignParams = url => {
  const campaignParamsKeys = [
    QUERY_PARAMS.ITM_CAMPAIGN,
    QUERY_PARAMS.ITM_ELEMENT,
    QUERY_PARAMS.ITM_CONTENT
  ];
  const from = url.match(/from=(.*)/);
  const fromParams =
    from && from.length > 1 ? new URL(decodeURIComponent(from[1])).search : '';
  const campaignValues = campaignParamsKeys.reduce((values, key) => {
    return values.concat(getQueryStringValue(fromParams || url, key) || '');
  }, []);
  return campaignValues.join('|');
};

export const isRememberMeSelected = idToken => {
  const decodedToken = JwtDecode(idToken);
  return decodedToken && decodedToken['https://accounts.ikea.com/isRemembered'];
};

export const isInsideIframe = () => {
  return (
    window.location !== window.parent.location ||
    window.self !== window.top ||
    window.frameElement
  );
};

export const isPopup = () => window.opener && window.opener !== window;

const isObjectEmpty = obj => Object.keys(obj).length === 0;

const getLoyaltyPrograms = authToken => {
  const payload = authToken.split('.')[1];
  return JSON.parse(window.atob(payload))[
    'https://accounts.ikea.com/loyaltyPrograms'
  ];
};

export const getUserType = async () => {
  if (window.flags && !isObjectEmpty(window.flags)) {
    if (window.flags.isLoggedIn !== 'true') return null;
    return window.flags.family?.toLowerCase() === 'true'
      ? PROFILE_TYPES.FAMILY
      : PROFILE_TYPES.REGULAR;
  }
  if (window.ikea) {
    const {
      authToken,
      isLoggedIn
    } = await window.ikea.authentication.isLoggedIn();
    if (!isLoggedIn) return null;
    return getLoyaltyPrograms(authToken).some(
      p => p === IKEA_FAMILY_LOYALTY_PROGRAM
    )
      ? PROFILE_TYPES.FAMILY
      : PROFILE_TYPES.REGULAR;
  }
  throw new Error('Could not find window.ikea or window.flags object.');
};

export const getIsLoggedIn = async () => {
  try {
    return (await getUserType()) !== null;
  } catch {
    return false;
  }
};
